import type { AgeBand } from './phrases';

/**
 * Placement cues for /ɹ/, each paired with the diagram the screen shows while it is spoken.
 *
 * The cues paraphrase the elements of /ɹ/ and the shaping strategies in Preston, Benway, Leece,
 * Hitchcock & McAllister (2020), "Tutorial: Motor-based treatment strategies for /r/
 * distortions", Table 1:
 * - an oral constriction: the tongue tip lifted, pointing up toward the roof just behind the gum
 *   ridge (retroflex; not an exaggerated curl, articulation-spec.md) or the middle of the tongue
 *   bunched up high, without touching the roof of the mouth. The cues aim the tip at the roof
 *   behind the bump, not at the bump: that is where a tap or trill (a rolled r), t, d and l touch
 *   (QA 2026-10-11);
 * - the tongue root pulled back toward the throat;
 * - the sides of the tongue braced against the upper back teeth, air down a central groove;
 * - the back of the tongue low (a high back is the /w/ gesture);
 * - lips only slightly rounded: a kiss-like rounding is the error, so "smile" or "spread your
 *   lips" are not used;
 * - shaping from a nearby sound: "ah" (point the tip up), "l" (slide the tip back and lift it
 *   off the roof), "eee" (pull the whole tongue back with the sides up).
 * Avoided on purpose: "growl" (invites a fricative or just loudness) and "back of the tongue up".
 * See docs/research/r-advice-and-diagrams.md.
 *
 * Diagrams are mid-sagittal sections restyled from Wikimedia Commons originals (art/README.md).
 * `shows` is the target gesture (drawn with a tick); `insteadOf`, when set, is the gesture the
 * child's error sounded like (drawn with a cross), so the pair teaches the difference.
 */

export type ErrorType = 'glide' | 'lateral' | 'distortion';

export type DiagramId = 'r-bunched' | 'r-retroflex' | 'w' | 'l' | 'tongue-sides';

export interface Hint {
  shows: DiagramId;
  insteadOf?: DiagramId;
  /** The cue's steps, as the card lists them while they are spoken (see Cue.step). */
  steps?: readonly HintStep[];
}

export interface HintStep {
  text: string;
  /** The child says this sound now, before the next step (e.g. "ee"). */
  try?: string;
}

/**
 * A cue that starts from a nearby sound ("Say the ee in see. Now slide your tongue back…") is
 * interactive: the coach says the first step, waits for the child to make that sound, then says
 * the second step. `text` is the whole cue (its caption when not split).
 */
export interface CueStep {
  first: string;
  /** The sound the child makes after the first step, as shown on screen ("ee"). */
  sound: string;
  then: string;
}

export interface Cue {
  text: string;
  hint: Hint;
  step?: CueStep;
}

const step = (first: string, sound: string, then: string): Pick<Cue, 'text' | 'step'> => ({
  text: `${first} ${then}`,
  step: { first, sound, then },
});

const youngerCues: Record<ErrorType, readonly Cue[]> = {
  glide: [
    {
      text: 'Your lips made a w. Let them round just a little, not like a kiss, and let your tongue do the work.',
      hint: { shows: 'r-bunched', insteadOf: 'w' },
    },
    {
      text: 'Keep the back of your tongue low, and lift the middle of it up high.',
      hint: { shows: 'r-bunched', insteadOf: 'w' },
    },
    {
      ...step(
        'Say ah.',
        'ah',
        'Now point your tongue tip up at the roof, just behind the bump, without touching it.',
      ),
      hint: { shows: 'r-retroflex', insteadOf: 'w' },
    },
    {
      ...step(
        'Say the ee in see.',
        'ee',
        'Now slide your whole tongue back, with the sides up against your back teeth.',
      ),
      hint: { shows: 'r-bunched', insteadOf: 'w' },
    },
    {
      text: 'Lips soft and still. Pull your tongue back toward your throat and make a long r sound.',
      hint: { shows: 'r-bunched', insteadOf: 'w' },
    },
  ],
  lateral: [
    {
      text: 'Your tongue tip touched the bump behind your teeth, like for l. Lift it off and let it float.',
      hint: { shows: 'r-retroflex', insteadOf: 'l' },
    },
    {
      ...step(
        'Hold an l sound.',
        'lll',
        'Now slide your tongue tip back and let it float off the roof into an r.',
      ),
      hint: { shows: 'r-retroflex', insteadOf: 'l' },
    },
    {
      text: 'Push the sides of your tongue up against your back teeth, like butterfly wings.',
      hint: { shows: 'tongue-sides' },
    },
    {
      text: 'Keep the sides of your tongue up, so the air goes down the middle, not out the sides.',
      hint: { shows: 'tongue-sides' },
    },
  ],
  distortion: [
    {
      text: 'Push the sides of your tongue up against your back teeth, like butterfly wings.',
      hint: { shows: 'tongue-sides' },
    },
    {
      ...step('Say the ee in see.', 'ee', 'Now slowly slide your tongue back into an r.'),
      hint: { shows: 'r-bunched' },
    },
    {
      text: 'Pull your tongue back toward your throat, and keep the middle up high.',
      hint: { shows: 'r-bunched' },
    },
    {
      ...step(
        'Say ah.',
        'ah',
        'Now point your tongue tip up at the roof, without touching anything.',
      ),
      hint: { shows: 'r-retroflex' },
    },
    {
      text: 'Hold your jaw still, round your lips just a little, and make the r sound long and strong.',
      hint: { shows: 'r-bunched' },
    },
  ],
};

const olderCues: Record<ErrorType, readonly Cue[]> = {
  glide: [
    {
      text: 'Your lips made it a w. Round them only slightly, not pushed out; let your tongue make the {sound}.',
      hint: { shows: 'r-bunched', insteadOf: 'w' },
    },
    {
      text: 'A w lifts the back of the tongue. For {sound}, keep the back low and raise the middle or the tip.',
      hint: { shows: 'r-bunched', insteadOf: 'w' },
    },
    {
      ...step(
        'Start with an ah.',
        'ah',
        'Now point your tip up to the roof, behind the gum ridge, without touching it.',
      ),
      hint: { shows: 'r-retroflex', insteadOf: 'w' },
    },
    {
      ...step(
        'Start with the ee in see.',
        'ee',
        'Now pull your tongue root back, with the sides against your upper molars.',
      ),
      hint: { shows: 'r-bunched', insteadOf: 'w' },
    },
  ],
  lateral: [
    {
      text: 'Your tongue tip is touching the ridge behind your teeth, as for l. Lift it so it touches nothing.',
      hint: { shows: 'r-retroflex', insteadOf: 'l' },
    },
    {
      ...step(
        'Hold an l.',
        'lll',
        'Now slide your tongue tip back and lift it off the roof, so it turns into {sound}.',
      ),
      hint: { shows: 'r-retroflex', insteadOf: 'l' },
    },
    {
      text: 'Brace the sides of your tongue against your upper molars, and let the air flow down the middle.',
      hint: { shows: 'tongue-sides' },
    },
    {
      text: 'Keep the sides of your tongue sealed against your molars, so no air escapes at the sides.',
      hint: { shows: 'tongue-sides' },
    },
  ],
  distortion: [
    {
      text: 'Brace the sides of your tongue against your upper back molars.',
      hint: { shows: 'tongue-sides' },
    },
    {
      ...step(
        'Start with the ee in see.',
        'ee',
        'Now pull your tongue back into the {sound}, while the sides stay up.',
      ),
      hint: { shows: 'r-bunched' },
    },
    {
      text: 'Pull your tongue root back toward your throat, and keep the middle of the tongue raised.',
      hint: { shows: 'r-bunched' },
    },
    {
      ...step(
        'Start with an ah.',
        'ah',
        'Now point the tip up to the roof, behind the gum ridge, without touching it.',
      ),
      hint: { shows: 'r-retroflex' },
    },
    {
      text: 'Hold the {sound} longer, with your jaw steady and your lips only slightly rounded.',
      hint: { shows: 'r-bunched' },
    },
  ],
};

export const CUES: Record<AgeBand, Record<ErrorType, readonly Cue[]>> = {
  younger: youngerCues,
  older: olderCues,
};
